import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Linkedin,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useI18n } from "@/i18n/I18nContext";
import { INSIGHTS } from "@/constants/testIds";
import { fetchInsight, fetchInsights, formatDate } from "@/lib/insightsApi";

const accentBg = (a) =>
  a === "gold"
    ? "from-amber-200 via-amber-100 to-white"
    : a === "navy"
    ? "from-slate-200 via-slate-100 to-white"
    : "from-cyan-200 via-cyan-100 to-white";

const WHATSAPP_URL = "https://wa.me/33672328408";
const LINKEDIN_URL = "https://linkedin.com/in/diallobirrou";

export default function InsightDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const i = t.insights;
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    fetchInsight(slug)
      .then((a) => {
        setArticle(a);
        setStatus("ok");
        const localTitle = a?.content?.[lang]?.title;
        if (localTitle) document.title = `${localTitle} — Birrou Advisory`;
      })
      .catch(() => setStatus("notfound"));

    fetchInsights()
      .then((list) => setRelated(list.filter((x) => x.slug !== slug).slice(0, 2)))
      .catch(() => setRelated([]));
  }, [slug, lang]);

  if (status === "loading") {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="container-x pt-40 pb-24 animate-pulse space-y-4">
          <div className="h-4 w-32 bg-slate-200 rounded" />
          <div className="h-12 w-3/4 bg-slate-200 rounded" />
          <div className="h-4 w-1/2 bg-slate-200 rounded" />
          <div className="h-64 bg-slate-100 rounded-2xl mt-8" />
        </div>
        <Footer />
      </main>
    );
  }

  if (status === "notfound" || !article) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-40 pb-24">
          <div className="container-x text-center max-w-xl mx-auto">
            <h1 className="font-display text-3xl font-semibold text-navy">
              {i.notFoundTitle}
            </h1>
            <p className="mt-4 text-slate-600">{i.notFoundDesc}</p>
            <Link to="/insights" className="mt-8 inline-flex btn-primary">
              {i.backToList} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const localized = article.content[lang] || article.content.fr;

  return (
    <main data-testid={INSIGHTS.detailPage} className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero */}
      <section
        className={`pt-32 lg:pt-40 pb-16 bg-gradient-to-br ${accentBg(
          article.accent
        )} relative overflow-hidden`}
      >
        <div className="absolute inset-0 -z-10 grid-bg opacity-30" />
        <div className="container-x max-w-3xl">
          <Link
            to="/insights"
            data-testid={INSIGHTS.backToList}
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-navy transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> {i.backToList}
          </Link>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold uppercase tracking-[0.22em] text-navy border border-slate-200">
            {article.tag[lang]}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-6 font-display font-semibold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
          >
            {localized.title}
          </motion.h1>
          <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed">
            {localized.excerpt}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> {formatDate(article.date, lang)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" /> {article.reading_minutes} {i.minRead}
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-white py-16">
        <div className="container-x max-w-3xl prose-article">
          {localized.blocks.map((b, k) => {
            if (b.type === "h2") {
              return (
                <h2
                  key={k}
                  className="mt-12 first:mt-0 font-display text-2xl sm:text-3xl font-semibold text-navy leading-tight"
                >
                  {b.text}
                </h2>
              );
            }
            if (b.type === "h3") {
              return (
                <h3
                  key={k}
                  className="mt-10 font-display text-xl sm:text-2xl font-semibold text-navy leading-tight"
                >
                  {b.text}
                </h3>
              );
            }
            if (b.type === "h4") {
              return (
                <h4
                  key={k}
                  className="mt-8 font-display text-base sm:text-lg font-semibold text-cyan-700 uppercase tracking-wide"
                >
                  {b.text}
                </h4>
              );
            }
            if (b.type === "ul") {
              return (
                <ul key={k} className="mt-6 space-y-3">
                  {b.items.map((it, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-slate-700 leading-relaxed"
                    >
                      <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.type === "quote") {
              return (
                <blockquote
                  key={k}
                  className="mt-8 pl-6 border-l-4 border-gold text-lg text-navy font-display leading-relaxed italic"
                >
                  {b.text}
                </blockquote>
              );
            }
            if (b.type === "cta") {
              return (
                <div
                  key={k}
                  data-testid={`insight-inline-cta-${k}`}
                  className="mt-12 p-7 lg:p-8 rounded-2xl bg-navy text-white relative overflow-hidden"
                >
                  <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-cyan-tech/15 blur-3xl" />
                  <div className="relative">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-tech font-semibold">
                      {b.overline || t.contact.overline}
                    </div>
                    {b.title && (
                      <h3 className="mt-2 font-display text-xl sm:text-2xl font-semibold leading-snug">
                        {b.title}
                      </h3>
                    )}
                    {b.subtitle && (
                      <p className="mt-3 text-slate-300 leading-relaxed">{b.subtitle}</p>
                    )}
                    <div className="mt-6 flex flex-wrap gap-3">
                      <a href={WHATSAPP_URL} target="_blank" rel="noreferrer noopener" className="btn-cyan">
                        <MessageCircle className="h-4 w-4" /> {b.buttonText || t.nav.cta}
                      </a>
                      <a href="/#contact" className="btn-ghost-light">
                        {t.contact.emailTitle} <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <p
                key={k}
                className="mt-6 first:mt-0 text-base sm:text-lg text-slate-700 leading-[1.8]"
              >
                {b.text}
              </p>
            );
          })}

          {/* CTA */}
          <div className="mt-16 p-7 rounded-2xl bg-navy text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-tech font-semibold">
                  {t.contact.overline}
                </div>
                <div className="mt-2 font-display text-xl font-semibold">
                  {t.contact.title}
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-cyan"
                >
                  <MessageCircle className="h-4 w-4" /> {t.nav.cta}
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-ghost-light"
                >
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-slate-50/70 py-16">
          <div className="container-x">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">
              {i.relatedTitle}
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="group block p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-lg hover:border-navy/30 transition-all"
                >
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{a.tag[lang]}</span>
                    <span>•</span>
                    <span>{formatDate(a.date, lang)}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-navy group-hover:text-cyan-700 transition-colors">
                    {a.titles[lang]}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                    {a.excerpts[lang]}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
